import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ProjectService } from '../../services/ProjectService/project';
import { AuthService } from '../../services/AuthService/auth';
import { ProjectResponse, ProjectStatus } from '../../models/project.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { FilterSelect, FilterOption } from '../../shared/components/filter-select/filter-select';
import { CreateProjectDialog } from '../../dialogs/create-project-dialog/create-project-dialog';

/** Backend caps `size` at 100 (see ProjectController#list) — the largest page we can ask for. */
const MAX_PAGE_SIZE = 100;

const STATUSES: ProjectStatus[] = ['ACTIVE', 'SUSPENDED', 'CANCELLED'];

function titleCase(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

@Component({
  selector: 'app-projects',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    StatusBadge,
    ErrorBanner,
    EmptyState,
    FilterSelect,
  ],
  templateUrl: './projects.html',
  styleUrl: './projects.scss',
})
export class Projects implements OnInit {
  statusOptions: FilterOption[] = STATUSES.map((s) => ({ value: s, label: titleCase(s) }));
  statusFilter: ProjectStatus | '' = '';

  projects = signal<ProjectResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  constructor(private projectService: ProjectService, private dialog: MatDialog, private auth: AuthService) {}

  // Matches the backend's hasRole('SUPERADMIN') on POST /api/projects: who may trade with whom is
  // a platform decision, same as adding participants.
  get canCreateProject(): boolean {
    return this.auth.getSession()?.role === 'SUPERADMIN';
  }

  ngOnInit(): void {
    this.load();
  }

  // The list endpoint has no status query param (ProjectController#list only takes page/size), and
  // `load()` already pulls every page, so filtering by status happens client-side over the full set.
  get filteredProjects(): ProjectResponse[] {
    const list = this.projects();
    return this.statusFilter ? list.filter((p) => p.status === this.statusFilter) : list;
  }

  get statusFilterLabel(): string {
    return this.statusFilter ? titleCase(this.statusFilter) : 'All statuses';
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    // The backend paginates (size capped at 100); fetch every page so the total and the
    // per-status breakdown reflect every project the caller can see, not just the first page.
    this.projectService.list(0, MAX_PAGE_SIZE).subscribe({
      next: (res) => {
        const totalPages = res.page?.total_pages ?? 1;

        if (totalPages <= 1) {
          this.isLoading.set(false);
          this.projects.set(res.data);
          return;
        }

        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);
        forkJoin(remainingPages.map((page) => this.projectService.list(page, MAX_PAGE_SIZE))).subscribe({
          next: (rest) => {
            this.isLoading.set(false);
            this.projects.set([...res.data, ...rest.flatMap((r) => r.data)]);
          },
          error: (err: HttpErrorResponse) => {
            this.isLoading.set(false);
            this.errorMessage.set(extractErrorMessage(err, 'Failed to load projects.'));
          },
        });
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load projects.'));
      },
    });
  }

  openCreateDialog(): void {
    this.dialog
      .open(CreateProjectDialog, { width: '480px' })
      .afterClosed()
      .subscribe((created) => {
        if (created) {
          this.load();
        }
      });
  }
}
